import { contentAudioKey, rhyme, type Scene, type Task } from "./game";

export type ChineseSpark = {
  id: string;
  line: string;
  source: string;
  meaning: string;
  spoken: string;
};

const CHINESE_SPARKS: ChineseSpark[] = [
  {
    id: "step",
    line: "千里之行，始于足下。",
    source: "《道德经》",
    meaning: "大本领，也是从认真做好这一小题开始的。",
    spoken: "千里之行，始于足下。大本领，也是从认真做好这一小题开始的。",
  },
  {
    id: "climb",
    line: "欲穷千里目，更上一层楼。",
    source: "王之涣《登鹳雀楼》",
    meaning: "多想一步，就能看得更远。",
    spoken: "欲穷千里目，更上一层楼。多想一步，就能看得更远。",
  },
  {
    id: "practice",
    line: "纸上得来终觉浅，绝知此事要躬行。",
    source: "陆游《冬夜读书示子聿》",
    meaning: "你刚才亲手摆一摆，就是在“躬行”。",
    spoken: "纸上得来终觉浅，绝知此事要躬行。你刚才亲手摆一摆，就是在躬行。",
  },
  {
    id: "sprout",
    line: "小荷才露尖尖角。",
    source: "杨万里《小池》",
    meaning: "今天的新本领，也像小荷一样冒出来了。",
    spoken: "小荷才露尖尖角。今天的新本领，也像小荷一样冒出来了。",
  },
  {
    id: "review",
    line: "温故而知新。",
    source: "《论语》",
    meaning: "把学过的再想一想，常常会有新发现。",
    spoken: "温故而知新。把学过的再想一想，常常会有新发现。",
  },
  {
    id: "small-steps",
    line: "不积跬步，无以至千里。",
    source: "荀子《劝学》",
    meaning: "每一道认真完成的题，都是向前的一小步。",
    spoken: "不积跬步，无以至千里。每一道认真完成的题，都是向前的一小步。",
  },
  {
    id: "thinking",
    line: "学而不思则罔，思而不学则殆。",
    source: "《论语》",
    meaning: "学和想要一起用，你刚才就在认真想。",
    spoken: "学而不思则罔，思而不学则殆。学和想要一起用，你刚才就在认真想。",
  },
  {
    id: "stream",
    line: "问渠那得清如许？为有源头活水来。",
    source: "朱熹《观书有感》",
    meaning: "每天学一点，新知识就像活水一样流进来。",
    spoken:
      "问渠那得清如许？为有源头活水来。每天学一点，新知识就像活水一样流进来。",
  },
];

const hash = (value: string) => {
  let result = 0;
  for (const char of value) result = (result * 31 + char.charCodeAt(0)) | 0;
  return Math.abs(result);
};

export function chineseSpark(seed: string | number) {
  return CHINESE_SPARKS[hash(String(seed)) % CHINESE_SPARKS.length];
}

export function chineseSparkForJourney(conceptIds: string[], mode: string, seed: string) {
  const ids = new Set(conceptIds);
  const preferred = mode === "review"
    ? "review"
    : [...ids].some((id) => id.startsWith("DIR-") || id.startsWith("MEAS-"))
      ? "practice"
      : [...ids].some((id) => id.startsWith("SORT-"))
        ? "thinking"
        : [...ids].some((id) => id.startsWith("MUL-") || id.startsWith("DIV-"))
          ? "small-steps"
          : chineseSpark(seed).id;
  return CHINESE_SPARKS.find((spark) => spark.id === preferred) || CHINESE_SPARKS[0];
}

const numberWords = [
  "zero",
  "one",
  "two",
  "three",
  "four",
  "five",
  "six",
  "seven",
  "eight",
  "nine",
];

const englishObjects: Record<
  Scene,
  { singular: string; plural: string; chinese: string }
> = {
  breakfast: { singular: "table", plural: "tables", chinese: "张桌子" },
  garden: { singular: "flower", plural: "flowers", chinese: "朵花" },
  harbor: { singular: "boat", plural: "boats", chinese: "条小船" },
};

export type EnglishSpark = {
  id: string;
  phrase: string;
  meaning: string;
  spoken: string;
};

export function englishSpark(task: Task): EnglishSpark {
  const count = Math.max(1, Math.min(9, task.a));
  const object = englishObjects[task.scene];
  const noun = count === 1 ? object.singular : object.plural;
  const phrase = `${numberWords[count]} ${noun}`;
  return {
    id: `${task.scene}-${count}`,
    phrase,
    meaning: `${count}${object.chinese}`,
    spoken: `英语小彩蛋。${phrase}，就是${count}${object.chinese}。`,
  };
}

export function mathFeedback(task: Task) {
  if (task.kind === "interactive")
    return task.explanation || "你把模型摆出来了，我们再看看每个数字表示什么。";
  if (task.kind === "choice") return task.explanation || `答对啦，答案是${task.answer}。`;
  if (task.kind === "share" && task.shareMode === "group-count")
    return `一共${task.a * task.b}块，每份${task.a}块，可以分成${task.b}份。`;
  if (!task.foundation)
    return `${task.a}组，每组${task.b}个，一共${task.a * task.b}个。${rhyme(task.a, task.b)}。`;
  if (task.kind === "count")
    return `你一个一个数清了，一共有${task.answer}个。`;
  if (task.kind === "addition")
    return `${task.a}加${task.b}等于${task.answer}。`;
  if (task.kind === "subtraction")
    return `${task.a + task.b}拿走${task.b}，还剩${task.answer}。`;
  if (task.kind === "place")
    return `${task.a}个十和${task.b}个一，合起来是${task.answer}。`;
  return `答对啦，答案是${task.answer}。`;
}

export function praiseFeedback(task: Task, helped: boolean) {
  if (helped) return "你愿意再试一次，真好！";
  if (task.templateId) {
    if (["GROUPS-TO-MUL", "ARRAY-TWO-VIEWS", "BUILD-FACT-PATTERN"].includes(task.templateId)) return "你把数量关系摆明白了！";
    if (task.templateId === "DIV-SHARE") return "你把点心分得一样多了！";
    if (task.templateId === "DIV-GROUPS") return "你按每份一样多分好，再数出了份数！";
    if (task.templateId === "FACT-MIXED") return "这是你自己想出来的！";
    return "你找到了题目里的数量线索！";
  }
  if (task.kind === "interactive") return "你找到了题目里的数量线索！";
  if (task.kind === "share" && task.shareMode === "group-count")
    return "你按每份一样多分好，再数出了份数！";
  if (task.kind === "share") return "你把点心分得一样多了！";
  if (task.kind === "choice") return "你找到了题目里的数量线索！";
  if (["group", "array", "jump"].includes(task.kind))
    return "你把数量关系摆明白了！";
  if (task.foundation && task.kind === "count") return "数得真仔细！";
  return "这是你自己想出来的！";
}

export const praiseAudioKey = (task: Task, helped: boolean) =>
  contentAudioKey("praise", `${task.templateId || task.kind}:${praiseFeedback(task, helped)}`);
export const hintAudioKey = (task: Task) =>
  task.hintText ? contentAudioKey("hint", task.hintText) : undefined;

export function mathFeedbackKey(task: Task) {
  if (task.kind === "choice" || task.kind === "interactive")
    return contentAudioKey(
      "feedback",
      `${task.templateId || task.kind}:${mathFeedback(task)}`,
    );
  if (task.kind === "share" && task.shareMode === "group-count")
    return contentAudioKey("feedback", `DIV-GROUPS:${mathFeedback(task)}`);
  return task.foundation
    ? `foundation-feedback-${task.kind}-${task.a}-${task.b}`
    : `math-feedback-${task.a}x${task.b}`;
}

export const LANGUAGE_AUDIO = [
  ...CHINESE_SPARKS.map((spark) => ({
    key: `language-${spark.id}`,
    text: spark.spoken,
  })),
  ...(["breakfast", "garden", "harbor"] as Scene[]).flatMap((scene) =>
    Array.from({ length: 9 }, (_, index) => {
      const count = index + 1;
      const object = englishObjects[scene];
      const noun = count === 1 ? object.singular : object.plural;
      const phrase = `${numberWords[count]} ${noun}`;
      return {
        key: `english-${scene}-${count}`,
        text: `英语小彩蛋。${phrase}，就是${count}${object.chinese}。`,
      };
    }),
  ),
];
